// Audio playback helper for capability voiceovers (ElevenLabs MP3s).
// Plays one audio at a time; exposes onVoiceState(start/end, idx) for UI.

let currentAudio = null;
let currentIdx = null;
let stateListeners = new Set();
// Pre-cache audio elements so the first play is instant
const cache = new Map();

function notify(state, idx) {
  stateListeners.forEach((cb) => cb(state, idx));
}

export function preloadVoices(audioMap) {
  // audioMap: { idx: '/assets/voice/file.mp3', ... }
  if (!audioMap) return;
  Object.entries(audioMap).forEach(([idx, src]) => {
    if (cache.has(src)) return;
    const a = new Audio();
    a.preload = 'auto';
    a.src = src;
    cache.set(src, a);
  });
}

function getAudio(src) {
  if (cache.has(src)) return cache.get(src);
  const a = new Audio();
  a.preload = 'auto';
  a.src = src;
  cache.set(src, a);
  return a;
}

export function speak(src, opts = {}) {
  if (!src) return false;
  const { volume = 1, idx = null, onEnd } = opts;
  cancel();
  try {
    const a = getAudio(src);
    a.volume = Math.max(0, Math.min(1, volume));
    a.currentTime = 0;
    currentAudio = a;
    currentIdx = idx;
    a.onplaying = () => notify('start', idx);
    a.onended = () => {
      notify('end', idx);
      currentAudio = null;
      currentIdx = null;
      onEnd && onEnd();
    };
    a.onerror = () => {
      notify('end', idx);
      currentAudio = null;
      currentIdx = null;
    };
    const p = a.play();
    if (p && typeof p.then === 'function') {
      p.catch((e) => {
        // Autoplay blocked or other error
        console.warn('[voice] play() rejected:', e?.message || e);
        notify('end', idx);
        currentAudio = null;
        currentIdx = null;
      });
    }
    return true;
  } catch (e) {
    console.error('[voice] speak failed', e);
    return false;
  }
}

export function cancel() {
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    } catch {}
    const idx = currentIdx;
    currentAudio = null;
    currentIdx = null;
    notify('end', idx);
  }
}

export function isSpeaking() {
  return !!currentAudio && !currentAudio.paused;
}

export function onVoiceState(cb) {
  stateListeners.add(cb);
  return () => stateListeners.delete(cb);
}
